<script setup lang="ts">
import { EButton } from '@erpx/ui'
import { useQuery } from '@tanstack/vue-query'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import {
  NOTIFICATION_KEYS,
  unreadCount as fetchUnreadCount,
  UNREAD_POLL_MS,
} from '@/modules/notifications/api'
import NotificationPanel from '@/modules/notifications/NotificationPanel.vue'
import { NOTIFICATIONS_PERMISSIONS } from '@/modules/notifications/permissions'
import { useAuthStore } from '@/stores/auth'

import './notifications.css'

/**
 * The bell in the command bar (CLAUDE.md §6's "notifications table + bell UI +
 * email", §11's app shell).
 *
 * Accessibility is the part that is easy to get wrong and this component is
 * where it is decided:
 *
 *  - **The count is in the accessible name**, not only in the red badge —
 *    "Notifications, 3 unread". The badge itself is `aria-hidden`, or the number
 *    is announced twice.
 *  - **A polite live region announces changes**, so a count that arrives while
 *    the user is working somewhere else on the page is heard rather than
 *    silently repainted.
 *  - **Focus returns to the bell when the panel closes**, however it closed.
 *    Losing focus to `<body>` after Escape strands a keyboard user at the top of
 *    the document.
 *
 * **No websocket** — none exists server-side. The badge polls on a 60s interval
 * and refetches when the window regains focus, which is the honest behaviour for
 * a REST endpoint and cheap on a COUNT over a partial index.
 *
 * A user without `notification.read` gets no bell at all rather than a disabled
 * one (§6: an action a user cannot perform is not rendered).
 */

const { t } = useI18n()
const auth = useAuthStore()
const route = useRoute()

const permitted = computed(() => auth.can(NOTIFICATIONS_PERMISSIONS.read))

const open = ref(false)
const wrapper = ref<HTMLElement | null>(null)

const { data } = useQuery({
  queryKey: NOTIFICATION_KEYS.unreadCount,
  queryFn: fetchUnreadCount,
  enabled: permitted,
  refetchInterval: UNREAD_POLL_MS,
  // The shared client turns this off app-wide so an approver switching tabs
  // does not fire a burst of refetches. The badge is the one query where
  // coming back to the tab is exactly when the answer must be current.
  refetchOnWindowFocus: true,
})

const count = computed(() => data.value ?? 0)

const bellLabel = computed(() =>
  count.value === 0
    ? t('notifications.bell.labelNone')
    : t('notifications.bell.label', { count: count.value }),
)

const liveText = computed(() =>
  count.value === 0 ? t('notifications.bell.statusNone') : t('notifications.bell.status', { count: count.value }),
)

/**
 * The trigger element, found through the DOM rather than a component ref.
 *
 * `EButton` renders through `<component :is>` with `inheritAttrs: false`, so a
 * template ref hands back a component instance whose `$el` is typed `any` — and
 * an `any` between a keyboard user and their focus is not worth the brevity.
 */
function focusTrigger(): void {
  wrapper.value?.querySelector('button')?.focus()
}

function close(returnFocus = true): void {
  if (!open.value) return
  open.value = false
  if (returnFocus) focusTrigger()
}

function toggle(): void {
  if (open.value) close()
  else open.value = true
}

/** A click anywhere outside the bell and its panel closes it, without stealing focus. */
function onPointerDown(event: Event): void {
  if (!open.value) return
  const target = event.target
  if (target instanceof Node && wrapper.value?.contains(target) === true) return
  close(false)
}

onMounted(() => document.addEventListener('pointerdown', onPointerDown, true))
onBeforeUnmount(() => document.removeEventListener('pointerdown', onPointerDown, true))

// Following a deep link leaves the panel floating over a screen it no longer
// belongs to. Focus stays where the router put it — the user asked to go there.
watch(() => route.fullPath, () => close(false))
</script>

<template>
  <div v-if="permitted" ref="wrapper" class="notif-bell">
    <EButton
      variant="ghost"
      size="sm"
      icon-only
      :aria-label="bellLabel"
      :aria-expanded="open"
      aria-haspopup="dialog"
      data-testid="notif-bell"
      @click="toggle"
    >
      <template #icon>
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.75"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M18 8a6 6 0 1 0-12 0c0 6-2 7-2 7h16s-2-1-2-7" />
          <path d="M13.7 20a2 2 0 0 1-3.4 0" />
        </svg>
      </template>
    </EButton>

    <span v-if="count > 0" class="notif-bell__badge erpx-numeric" aria-hidden="true" data-testid="notif-unread-count">
      {{ count }}
    </span>

    <span class="visually-hidden" role="status" data-testid="notif-live">{{ liveText }}</span>

    <NotificationPanel
      v-if="open"
      :unread-count="count"
      @close="close()"
      @navigate="close(false)"
    />
  </div>
</template>
